// frontend/src/components/Texpact/QCRLookup.jsx
//
// Menu page: user types an LMC code, sees lot info, downloads the QCR Word file.

import React, { useState, useRef } from 'react';
import { FiSearch, FiDownload, FiAlertCircle, FiCheckCircle, FiPackage } from 'react-icons/fi';
import { useTranslation } from '../../context/TranslationContext';
import apiClient from '../../api/client';
import './QCRLookup.css';

const QCRLookup = () => {
  const { t } = useTranslation();
  const [lotCode,  setLotCode]  = useState('');
  const [info,     setInfo]     = useState(null);   // lot info from /api/qcr/info
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [downloading, setDownloading] = useState(false);
  const inputRef = useRef(null);

  const handleSearch = async (e) => {
    e?.preventDefault();
    const code = lotCode.trim().toUpperCase();
    if (!code) return;

    setLoading(true);
    setError('');
    setInfo(null);

    try {
      const res = await apiClient.get(`/texpact/qcr/info/${code}`);
      if (res.data.success) {
        setInfo(res.data.data);
      } else {
        setError(res.data.error || 'Erro desconhecido');
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Código de lote não encontrado';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!info?.file_exists) return;
    setDownloading(true);
    try {
      const res = await apiClient.get(`/texpact/qcr/docx/${info.lot_code}`, {
        responseType: 'blob',
      });
      const url  = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href  = url;
      link.setAttribute('download', info.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Erro ao descarregar ficheiro');
    } finally {
      setDownloading(false);
    }
  };

  const handleClear = () => {
    setLotCode('');
    setInfo(null);
    setError('');
    inputRef.current?.focus();
  };

  return (
    <div className="qcr-page">
      <div className="page-header page-header--compact">
        <h2>QCR — Relatório de Controlo de Qualidade</h2>
      </div>

      <div className="qcr-card">
        <p className="qcr-hint">
          Introduza o código do lote (LMC) para obter o relatório QCR correspondente.
        </p>

        {/* Search form */}
        <form onSubmit={handleSearch} className="qcr-form">
          <div className="qcr-input-wrap">
            <input
              ref={inputRef}
              type="text"
              className="qcr-input"
              value={lotCode}
              onChange={e => setLotCode(e.target.value.toUpperCase())}
              placeholder="Ex: LMC2605738"
              autoFocus
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <button type="submit" className="btn-primary" disabled={loading || !lotCode.trim()}>
            <FiSearch size={15} style={{ marginRight: 6 }} />
            {loading ? 'A pesquisar…' : 'Pesquisar'}
          </button>
          {(info || error) && (
            <button type="button" className="btn-secondary" onClick={handleClear}>
              Limpar
            </button>
          )}
        </form>

        {/* Error */}
        {error && (
          <div className="qcr-message qcr-message--error">
            <FiAlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {/* Result card */}
        {info && (
          <div className="qcr-result">
            <div className="qcr-result-header">
              <FiPackage size={18} style={{ color: 'var(--primary)', flexShrink: 0 }} />
              <div className="qcr-result-title">
                <span className="qcr-lot-code">{info.lot_code}</span>
                {info.product_code && (
                  <span className="qcr-product">{info.product_code} — {info.product_name}</span>
                )}
              </div>
            </div>

            <div className="qcr-result-grid">
              <div className="qcr-field">
                <span className="qcr-label">PO Code</span>
                <span className="qcr-value qcr-mono">{info.po_code_full || `${info.po_code}-${info.po_line}`}</span>
              </div>
              {info.shipment_doc && (
                <div className="qcr-field">
                  <span className="qcr-label">Documento de envio</span>
                  <span className="qcr-value qcr-mono">{info.shipment_doc}</span>
                </div>
              )}
              {info.quantity && (
                <div className="qcr-field">
                  <span className="qcr-label">Quantidade</span>
                  <span className="qcr-value">{parseFloat(info.quantity).toFixed(2)} {info.unit_measure}</span>
                </div>
              )}
              <div className="qcr-field">
                <span className="qcr-label">Ficheiro QCR</span>
                <span className="qcr-value qcr-mono">{info.file_name}</span>
              </div>
            </div>

            {info.file_exists ? (
              <button
                className="btn-primary qcr-download-btn"
                onClick={handleDownload}
                disabled={downloading}
              >
                <FiDownload size={15} style={{ marginRight: 6 }} />
                {downloading ? 'A descarregar…' : 'Descarregar QCR (.docx)'}
              </button>
            ) : (
              <div className="qcr-message qcr-message--warning">
                <FiAlertCircle size={15} />
                <span>Ficheiro QCR ainda não disponível para este lote ({info.file_name})</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default QCRLookup;